import { TiledLayer, TiledLayerObjectgroup, TiledMap } from "tiled-types";

export enum MapType {
  ROOM = "room",
  DELUXE_ROOM = "deluxe_room",
  MY_HOUSE = "my_house",
}

interface MapConfig {
  key: MapType;
  scale: number;
  path?: string;
  startPoint: [number, number];
  startRange: number;
}

export const MAP_CONFIGS: Record<MapType, MapConfig> = {
  [MapType.ROOM]: {
    key: MapType.ROOM,
    scale: 3,
    path: "/game/map/room.json",
    startPoint: [11, 11],
    startRange: 3,
  },
  [MapType.DELUXE_ROOM]: {
    key: MapType.DELUXE_ROOM,
    scale: 1.5,
    path: "/game/map/deluxe_room.json",
    startPoint: [15, 19],
    startRange: 2,
  },
  [MapType.MY_HOUSE]: {
    key: MapType.MY_HOUSE,
    scale: 1,
    startPoint: [24, 8],
    startRange: 2,
  },
};

export const useMap = (scene: Phaser.Scene, key: MapType, json: TiledMap) => {
  const config = MAP_CONFIGS[key];

  const load = () => {
    scene.cache.tilemap.add(key, { format: 1, data: json });
    // 2. 解析tilemapJson，加载tileset图片 && sprite
    for (const tile of json.tilesets) {
      if (tile.properties?.find((p) => p.name === "type")?.value === "sprite") {
        scene.load.spritesheet(tile.name, tile.image!.replace("../", ""), {
          frameWidth: tile.tilewidth,
          frameHeight: tile.tileheight,
        });
      } else {
        scene.load.image(tile.name, tile.image!.replace("../", ""));
      }
    }
  };

  const create = () => {
    // 1. 创建地图
    const map = scene.make.tilemap({
      key,
      tileWidth: json.tilewidth,
      tileHeight: json.tileheight,
    });
    // 2. 装载tileset图片
    const tilesets: Phaser.Tilemaps.Tileset[] = [];
    for (const tile of json.tilesets) {
      const tileset = map.addTilesetImage(tile.name, tile.name);
      if (tileset) {
        tilesets.push(tileset);
      }
    }
    // 3. 创建layer
    const tileLayers = flattenTileLayers(json.layers);
    const layers: Phaser.Tilemaps.TilemapLayer[] = [];
    for (const layer of tileLayers) {
      const tileLayer = map.createLayer(layer.name, tilesets, layer.x, layer.y);
      if (tileLayer) {
        tileLayer.visible = layer.visible;
        tileLayer.setScale(config.scale);
        layers.push(tileLayer);
      }
    }

    return {
      map,
      tilesets,
      tileLayers: layers,
    };
  };

  const createObjects = (map: Phaser.Tilemaps.Tilemap) => {
    const objectLayers = getObjectLayers(json.layers as TiledLayer[]);
    const mapSprites: Phaser.GameObjects.Sprite[] = [];
    for (const layer of objectLayers) {
      for (const object of layer.objects) {
        if (!object.name) continue;

        const sprites =
          (map.createFromObjects(layer.name, {
            gid: object.gid,
            key: object.name,
            frame: 0,
          }) as unknown as Phaser.GameObjects.Sprite[]) ?? [];

        sprites.forEach((sprite) => {
          const objectWidth = object.width * config.scale;
          const objectHeight = object.height * config.scale;
          const container = scene.add.container(
            object.x * config.scale + objectWidth / 2,
            object.y * config.scale - objectHeight / 2
          );
          container.setDepth(12);
          container.add(sprite);
          sprite.setScale(config.scale);
          sprite.setPosition(0, 0);
        });
        mapSprites.push(...sprites);
      }
    }
    return mapSprites;
  };

  return {
    load,
    create,
    createObjects,
    mapScale: config.scale,
  };
};

/**
 * dfs 遍历roomJson.layers，仅处理type为tilelayer的layer
 * 包含组时：GroupName/layerName
 * 不包含组时：layerName
 */
function flattenTileLayers(
  layers: TiledLayer[],
  groupName: string = ""
): TiledLayer[] {
  const tileLayers: TiledLayer[] = [];
  for (const layer of layers) {
    if (layer.type === "tilelayer") {
      // 创建layer的副本并修改name
      const modifiedLayer = { ...layer };
      if (groupName) {
        modifiedLayer.name = `${groupName}/${layer.name}`;
      }
      tileLayers.push(modifiedLayer);
    }
    if (layer.type === "group" && layer.layers) {
      // 当type为group时，拼接group.name
      const newGroupName = groupName
        ? `${groupName}/${layer.name}`
        : layer.name;
      tileLayers.push(...flattenTileLayers(layer.layers, newGroupName));
    }
  }
  return tileLayers;
}

function getObjectLayers(layers: TiledLayer[]): TiledLayerObjectgroup[] {
  return layers.filter(
    (layer) => layer.type === "objectgroup"
  ) as TiledLayerObjectgroup[];
}
